import {
  statusIslandIcons,
  type StatusIslandViewModel,
} from '@blocksuite/affine/shared/services';
import { useCallback, useMemo, useState } from 'react';

import type { SignedPdfVersionInfo } from '../../../desktop/dialogs/setting/workspace-setting/signatures/use-workspace-pdf-signatures';
import { StatusIsland } from './embedding-badge-react';
import {
  getSignedPdfBadgeLabel,
  getSignedPdfBadgeTitle,
} from './pdf-signed-version-labels';
import {
  describeSignedPdfVersion,
  getSignatureValidationTone,
  getSignedPdfVersionTrustNote,
} from './pdf-signing-status-labels';
import * as styles from './styles.css';

/**
 * The mark on a signed PDF copy. Collapsed it says that the copy is signed and
 * how its check went; opened it tells who signed, when, to which standard and
 * with which certificate.
 */
export function PdfSignedBadge({ version }: { version: SignedPdfVersionInfo }) {
  const [expanded, setExpanded] = useState(false);
  const collapse = useCallback(() => setExpanded(false), []);

  const vm = useMemo<StatusIslandViewModel>(() => {
    const tone = getSignatureValidationTone(version.validationState);
    return {
      tone,
      icon:
        tone === 'success'
          ? statusIslandIcons.verified
          : statusIslandIcons.alert,
      label: getSignedPdfBadgeLabel(version),
      labelVisible: true,
      stateClass: `signed-${version.validationState}`,
      expanded,
      title: getSignedPdfBadgeTitle(),
      rows: describeSignedPdfVersion(version),
      body: getSignedPdfVersionTrustNote(version) ?? undefined,
      onClick: event => {
        event.stopPropagation();
        setExpanded(value => !value);
      },
    };
  }, [expanded, version]);

  return (
    <StatusIsland
      vm={vm}
      className={styles.pdfSignedBadgeHost}
      testId="pdf-signed-badge"
      onOutsidePointerDown={expanded ? collapse : undefined}
    />
  );
}
